<template>
  <div :style="{ direction: currentLanguage === 'ar' ? 'rtl' : 'ltr', background: '#F6F9FB', minHeight: '100vh' }">
    
    <!-- ============ HEADER & HERO ============ -->
    <section class="hero-section">
      <div class="eyebrow">
        {{ currentLanguage === 'en' ? '✨ 100% Free Tool — Works in your browser' : '✨ أداة مجانية بالكامل — تعمل داخل متصفحك' }}
      </div>
      <h1>
        {{ currentLanguage === 'en' ? 'Free Online Image Editor' : 'محرر الصور المجاني أونلاين' }}
      </h1>
      <p>
        {{ currentLanguage === 'en' 
          ? 'Edit and design your photos easily without registration. Add text, shapes, and movable layers. Adjust sizes and filters, then download your design in PNG, JPG, or WebP formats.' 
          : 'عدّل وصمّم صورك بسهولة بدون تسجيل: ارفع الصورة، أضف نصوصًا وأشكالًا وطبقات قابلة للتحريك، غيّر المقاس والفلاتر، ثم حمّل التصميم بصيغ PNG أو JPG أو WebP.' }}
      </p>
    </section>

    <!-- ============ TOOL COMPONENT ============ -->
    <div class="pt-2 pb-5">
      <ImageEditorCom />
    </div>

    <!-- ============ HELP SECTION ============ -->
    <section class="help-section">
      <div class="help-header text-center">
        <h2 class="help-title">
          {{ currentLanguage === 'en' ? 'Image Editor Guide and FAQs' : 'دليل محرر الصور والأسئلة الشائعة' }}
        </h2>
        <p class="help-subtitle">
          {{ currentLanguage === 'en'
            ? 'Learn how to use the Free Image Editor tool'
            : 'تعرف على كيفية استخدام أداة محرر الصور المجاني' }}
        </p>
      </div>

      <!-- Tabs -->
      <nav class="tabs-nav" role="tablist" :aria-label="currentLanguage === 'en' ? 'Help topics' : 'موضوعات المساعدة'">
        <button type="button" role="tab" id="image-editor-faq-tab" aria-controls="image-editor-faq-panel" :aria-selected="activeTab === 'faq'" :class="['tab-btn', activeTab === 'faq' && 'active']" @click="activeTab='faq'" @keydown.right.prevent="activeTab='guide'">
          {{ currentLanguage === 'en' ? 'FAQ' : 'الأسئلة الشائعة' }}
        </button>
        <button type="button" role="tab" id="image-editor-guide-tab" aria-controls="image-editor-guide-panel" :aria-selected="activeTab === 'guide'" :class="['tab-btn', activeTab === 'guide' && 'active']" @click="activeTab='guide'" @keydown.left.prevent="activeTab='faq'">
          {{ currentLanguage === 'en' ? 'Guide' : 'دليل الاستخدام' }}
        </button>
      </nav>

      <!-- FAQ -->
      <div v-show="activeTab === 'faq'" id="image-editor-faq-panel" role="tabpanel" aria-labelledby="image-editor-faq-tab" class="tab-pane">
        <div
          v-for="(item, i) in currentFaq"
          :key="i"
          class="faq-item"
          :class="{ open: openFaq === i }"
        >
          <div class="faq-question" @click="openFaq = openFaq === i ? null : i">
            {{ item.q }}
            <span class="faq-toggle">{{ openFaq === i ? '−' : '+' }}</span>
          </div>
          <div class="faq-answer">{{ item.a }}</div>
        </div>
      </div>

      <!-- Guide -->
      <div v-show="activeTab === 'guide'" id="image-editor-guide-panel" role="tabpanel" aria-labelledby="image-editor-guide-tab" class="tab-pane">
        <div class="search-box">
          <input
            type="text"
            v-model="guideSearch"
            :placeholder="currentLanguage === 'en' ? 'Search guide steps...' : 'ابحث في خطوات الاستخدام...'"
          />
        </div>

        <div v-for="(step, i) in filteredSteps" :key="i" class="step-card">
          <h3>{{ i + 1 }}. {{ step.title }}</h3>
          <p>{{ step.desc }}</p>
        </div>

        <p v-if="filteredSteps.length === 0" class="no-results">
          {{ currentLanguage === 'en' ? 'No results found' : 'لا توجد نتائج تطابق بحثك' }}
        </p>
      </div>
    </section>

  </div>
</template>

<script>
import ImageEditorCom from '@/components/ImageeditorCom.vue'

export default {
  name: 'ImageEditor',
  components: { ImageEditorCom },

  data() {
    return {
      currentLanguage: this.$store.state.currentLanguage,
      activeTab: 'faq',
      openFaq: null,
      guideSearch: '',

      // FAQ
      faqEn: [
        { q: 'Is the tool completely free?', a: 'Yes, the tool is 100% free and does not require you to create an account or log in.' },
        { q: 'Are images uploaded to a server?', a: 'No. All edits happen locally inside your browser ensuring total privacy. The final image is downloaded directly to your device.' },
        { q: 'Can I edit text after adding it?', a: 'Yes, text and shapes are treated as movable layers. You can select, move, scale, and edit them anytime before downloading.' },
        { q: 'What formats are supported for download?', a: 'You can export your final design in high-quality PNG, JPG, or WebP formats.' }
      ],
      faqAr: [
        { q: 'هل الأداة مجانية بالكامل؟', a: 'نعم، الأداة مجانية ولا تحتاج إلى إنشاء حساب أو تسجيل دخول.' },
        { q: 'هل يتم رفع الصور إلى الخادم؟', a: 'لا. التعديلات تتم داخل متصفحك باستخدام تقنية Canvas للحفاظ على خصوصيتك، ثم يتم تحميل الصورة مباشرة على جهازك.' },
        { q: 'هل يمكن تعديل النصوص بعد إضافتها؟', a: 'نعم، النصوص والأشكال تظل كطبقات قابلة للتحديد والتحريك والتعديل قبل تحميل الصورة النهائية.' },
        { q: 'ما صيغ التحميل المتاحة؟', a: 'يمكن تحميل الصورة النهائية بصيغ عالية الجودة مثل PNG أو JPG أو WebP.' }
      ],

      // Guide
      stepsEn: [
        { title: 'Upload Image', desc: 'Drag and drop your image into the workspace or click to choose a file. You can also start with a blank canvas.' },
        { title: 'Resize & Filter', desc: 'Go to the Adjust tab to crop, resize, or apply image filters like brightness, contrast, and saturation.' },
        { title: 'Add Elements', desc: 'Switch to the Elements tab to insert text, rectangles, circles, or use the freehand drawing tool.' },
        { title: 'Manage Layers', desc: 'Select any element on the canvas to adjust its scale, rotation, color, or opacity instantly.' },
        { title: 'Export & Download', desc: 'Choose your preferred format (PNG, JPG, WebP) and quality, then click download to save your design.' }
      ],
      stepsAr: [
        { title: 'رفع الصورة', desc: 'اسحب وأفلت صورتك في مساحة العمل أو اضغط لاختيار ملف. يمكنك أيضاً البدء بلوحة فارغة.' },
        { title: 'تغيير المقاس والفلاتر', desc: 'اذهب لتبويب (الصورة) لتغيير الأبعاد أو إضافة فلاتر مثل السطوع، التباين، والتشبع.' },
        { title: 'إضافة العناصر', desc: 'انتقل لتبويب (العناصر) لإضافة نصوص، مستطيلات، دوائر، أو استخدام أداة الرسم الحر.' },
        { title: 'إدارة الطبقات', desc: 'حدد أي عنصر على اللوحة لتعديل حجمه، دورانه، لونه، أو شفافيته بكل سهولة.' },
        { title: 'التصدير والتحميل', desc: 'اختر الصيغة المناسبة (PNG, JPG, WebP) والجودة المطلوبة، ثم اضغط على تحميل لحفظ التصميم.' }
      ]
    }
  },

  computed: {
    currentFaq() {
      return this.currentLanguage === 'en' ? this.faqEn : this.faqAr;
    },
    filteredSteps() {
      const steps = this.currentLanguage === 'en' ? this.stepsEn : this.stepsAr;
      if (!this.guideSearch) return steps;
      return steps.filter(s =>
        (s.title + s.desc).toLowerCase().includes(this.guideSearch.toLowerCase())
      );
    }
  },

  watch: {
    '$store.state.currentLanguage'(newLang) {
      this.currentLanguage = newLang;

      const route = newLang === 'en' ? 'image-editor-en' : 'image-editor-ar';
      if (this.$route.name !== route) {
        this.$router.push({ name: route }).catch(()=>{});
      }
    }
  }
}
</script>

<style scoped lang='scss'>
// ── Hero Section ─────────────────────────────────────────────────
.hero-section {
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px 20px;
  text-align: center;
  font-family: 'Cairo', 'DM Sans', sans-serif;

  .eyebrow {
    display: inline-flex;
    gap: 8px;
    align-items: center;
    border: 1px solid #dbe6ff;
    background: #fff;
    color: #3a7afe;
    padding: 8px 16px;
    border-radius: 999px;
    font-weight: 800;
    font-size: 0.85rem;
    margin-bottom: 15px;
  }

  h1 {
    font-size: clamp(1.8rem, 4vw, 2.8rem);
    line-height: 1.2;
    margin: 0 0 15px;
    font-weight: 800;
    color: #24365c;
  }

  p {
    margin: 0 auto;
    color: #716c80;
    font-size: 1rem;
    line-height: 1.7;
    max-width: 700px;
  }
}

// ── General Typography ───────────────────────────────────────────
p {
  font-size: 14px;
  color: #716c80;
}

.text-center {
  text-align: center;
}

.pt-2 { padding-top: 0.5rem; }
.pb-5 { padding-bottom: 3rem; }

// ── Help Section ─────────────────────────────────────────────────
.help-section {
  max-width: 900px;
  margin: 0 auto 0px;
  padding: 20px;
  font-family: 'Cairo', 'DM Sans', sans-serif;
}

.help-header {
  margin-bottom: 40px;
}

.help-title {
  font-weight: 800;
  font-size: 2rem;
  color: #1e1b4b;
  margin-bottom: 8px;
}

.help-subtitle {
  color: #64748b;
  font-size: 14px;
}

// ── Tabs ──────────────────────────────────────────────────────────
.tabs-nav {
  display: flex;
  background: white;
  border-radius: 15px;
  overflow: hidden;
  margin-bottom: 25px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.tab-btn {
  flex: 1;
  padding: 18px;
  border: none;
  background: none;
  font-family: inherit;
  font-weight: 700;
  cursor: pointer;
  transition: 0.3s;
  font-size: 1rem;
  color: #64748b;
  border-bottom: 3px solid transparent;

  &.active {
    color: #3a7afe;
    background: #fdfdff;
    border-bottom: 3px solid #3a7afe;
  }
}

.tab-pane {
  animation: fadeIn 0.4s ease;
}

// ── FAQ Items ─────────────────────────────────────────────────────
.faq-item {
  background: white;
  border-radius: 15px;
  margin-bottom: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
}

.faq-question {
  padding: 18px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: white;
  color: #1e1b4b;
  font-size: 14px;
  transition: color 0.3s;

  .faq-toggle {
    font-size: 20px;
    color: #64748b;
    flex-shrink: 0;
    margin-right: 8px;
    margin-left: 8px;
    line-height: 1;
  }
}

.faq-answer {
  padding: 0 18px;
  max-height: 0;
  overflow: hidden;
  transition: max-height 0.3s ease-out, padding 0.3s;
  color: #64748b;
  font-size: 14px;
  line-height: 1.7;
}

.faq-item.open {
  .faq-question {
    color: #3a7afe;
  }
  .faq-answer {
    max-height: 300px;
    padding: 0 18px 18px;
  }
}

// ── Search Box ────────────────────────────────────────────────────
.search-box {
  position: relative;
  margin-bottom: 30px;

  input {
    width: 100%;
    padding: 15px 45px 15px 20px;
    border-radius: 12px;
    border: 2px solid #e2e8f0;
    font-family: inherit;
    font-size: 1rem;
    box-sizing: border-box;
    outline: none;
    transition: 0.3s;
    color: #1e1b4b;

    &:focus {
      border-color: #3a7afe;
      box-shadow: 0 0 10px rgba(99, 102, 241, 0.1);
    }

    &::placeholder {
      color: #94a3b8;
    }
  }

  .search-icon {
    position: absolute;
    right: 15px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 1.1rem;
    pointer-events: none;
  }
}

[dir="rtl"] .search-box {
  input {
    padding: 15px 20px 15px 45px;
  }
  .search-icon {
    right: auto;
    left: 15px;
  }
}

// ── Step Cards ────────────────────────────────────────────────────
.step-card {
  background: white;
  padding: 25px;
  border-radius: 20px;
  margin-bottom: 20px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
  border-right: 5px solid #3a7afe;

  h2 {
    margin-top: 0;
    font-size: 1.2rem;
    color: #3a7afe;
    font-weight: 700;
    margin-bottom: 10px;
  }

  p {
    margin-bottom: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.7;
  }
}

[dir="rtl"] .step-card {
  border-right: none;
  border-left: 5px solid #3a7afe;
}

.no-results {
  text-align: center;
  padding: 40px;
  color: #64748b;
  font-size: 14px;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to   { opacity: 1; transform: translateY(0); }
}

@media (max-width: 769px) {
  .help-section { padding: 10px; }
  .tab-btn { padding: 12px; font-size: 0.9rem; }
  .step-card { padding: 15px; }
  .help-title { font-size: 1.5rem; }
}
</style>
